import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Play, Loader2, FileText, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WLLSAnalysisTabProps {
  fileId: string;
  onAnalysisComplete: () => void;
}

// Helper function to process raw data to WLLS tokens
const processRawDataToWlls = (rawData: any) => {
  const tokens: string[] = [];
  
  // Handle both JSON array and string formats
  let data: any[] = [];
  if (typeof rawData === 'string') {
    // If it's a CSV string, parse it
    const lines = rawData.trim().split('\n');
    const headers = lines[0]?.split(',').map(h => h.trim().replace(/"/g, '')) || [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }
  } else if (Array.isArray(rawData)) {
    // If it's already a JSON array, use it directly
    data = rawData;
  } else {
    return [];
  }
  
  for (const row of data) {
    const type = (row.Type || row.type || '').toString().toLowerCase();
    const pnl = row['P&L USD'] || row.pnl || row['P&L'] || '';
    
    if (!type || pnl === '') continue;
    
    // Determine win/loss (1 = win, 0 = loss)
    let winLoss = '';
    const pnlValue = parseFloat(pnl.toString().replace(/[,$]/g, ''));
    if (!isNaN(pnlValue)) {
      winLoss = pnlValue > 0 ? '1' : '0';
    } else if (pnl.toString().toLowerCase().includes('win')) {
      winLoss = '1';
    } else if (pnl.toString().toLowerCase().includes('loss')) {
      winLoss = '0';
    } else {
      continue; // Skip invalid entries
    }
    
    // Determine long/short direction
    let direction = '';
    if (type.includes('long') || type.includes('buy')) {
      direction = 'L';
    } else if (type.includes('short') || type.includes('sell')) {
      direction = 'S';
    } else {
      continue; // Skip invalid entries
    }
    
    tokens.push(winLoss + direction);
  }
  
  return tokens;
};

export const WLLSAnalysisTab: React.FC<WLLSAnalysisTabProps> = ({
  fileId,
  onAnalysisComplete
}) => {
  const [minLength, setMinLength] = useState(3);
  const [maxLength, setMaxLength] = useState(7);
  const [minFrequency, setMinFrequency] = useState(2);
  const [binaryMode, setBinaryMode] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [complexityWarning, setComplexityWarning] = useState<string | null>(null);

  const { data: file, refetch } = useQuery({
    queryKey: ['wlls-file-analysis', fileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wlls_files')
        .select('filename, analysis_status, total_trades, raw_data, error_message')
        .eq('id', fileId)
        .single();
      
      if (error) throw error;
      return data;
    },
    refetchInterval: isAnalyzing ? 2000 : false
  });

  // Calculate complexity estimate
  const calculateComplexity = (totalTrades: number, minLen: number, maxLen: number) => {
    if (!totalTrades) return 0;
    const avgLength = (minLen + maxLen) / 2;
    return Math.floor(totalTrades * avgLength);
  };

  // Update complexity warning when parameters change
  useEffect(() => {
    if (file?.total_trades) {
      const complexity = calculateComplexity(file.total_trades, minLength, maxLength);
      if (complexity > 200000) {
        setComplexityWarning(`Very high complexity (~${Math.floor(complexity / 1000)}k patterns). Analysis may take several seconds.`);
      } else if (complexity > 100000) {
        setComplexityWarning(`High complexity (~${Math.floor(complexity / 1000)}k patterns). Analysis may take a few seconds.`);
      } else {
        setComplexityWarning(null);
      }
    }
  }, [file?.total_trades, minLength, maxLength]);

  const runAnalysis = async () => {
    if (!file?.raw_data) {
      toast.error('No raw data available for this file');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Process raw data to tokens on client side
      const tokens = processRawDataToWlls(file.raw_data);
      
      if (tokens.length === 0) {
        toast.error('No valid trading data found in raw data');
        return;
      }

      // Update file with processed data and set status to processing
      const { error: updateError } = await supabase
        .from('wlls_files')
        .update({
          processed_data: tokens,
          total_trades: tokens.length,
          analysis_status: 'processing'
        })
        .eq('id', fileId);

      if (updateError) {
        console.error('Update error:', updateError);
        toast.error('Failed to update file data');
        return;
      }

      // Call the RPC function for pattern analysis
      const { data, error } = await supabase.rpc('wlls_analyze_patterns', {
        p_file_id: fileId,
        p_min_length: minLength,
        p_max_length: maxLength,
        p_min_frequency: minFrequency,
        p_binary: binaryMode
      });

      if (error) {
        console.error('Analysis error:', error);
        toast.error('Analysis failed: ' + error.message);
        return;
      }

      // Show success message with pattern count and timing
      if (data && data.length > 0) {
        const result = data[0];
        toast.success(`Analysis completed! Found ${result.patterns_created} patterns in ${result.analysis_time_ms}ms`);
      } else {
        toast.success('Analysis completed successfully!');
      }
      
      onAnalysisComplete();
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analysis failed: ' + (error as Error).message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!file) return null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800">Processing</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Error</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* File Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {file.filename}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Status: </span>
              {getStatusBadge(file.analysis_status)}
            </div>
            <div>
              <span className="text-muted-foreground">Total Trades: </span>
              {file.total_trades || 0}
            </div>
          </div>
          
          {file.error_message && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Analysis Error</span>
              </div>
              <p className="text-sm text-red-600 mt-1">{file.error_message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analysis Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="minLength">Min Pattern Length</Label>
              <Input
                id="minLength"
                type="number"
                value={minLength}
                onChange={(e) => setMinLength(parseInt(e.target.value))}
                min={2}
                max={10}
                disabled={isAnalyzing}
              />
            </div>
            <div>
              <Label htmlFor="maxLength">Max Pattern Length</Label>
              <Input
                id="maxLength"
                type="number"
                value={maxLength}
                onChange={(e) => setMaxLength(parseInt(e.target.value))}
                min={2}
                max={10}
                disabled={isAnalyzing}
              />
            </div>
            <div>
              <Label htmlFor="minFrequency">Min Frequency</Label>
              <Input
                id="minFrequency"
                type="number"
                value={minFrequency}
                onChange={(e) => setMinFrequency(parseInt(e.target.value))}
                min={1}
                max={100}
                disabled={isAnalyzing}
              />
            </div>
          </div>

          {/* Binary Mode Toggle */}
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg border">
            <Switch
              id="binaryMode"
              checked={binaryMode}
              onCheckedChange={setBinaryMode}
              disabled={isAnalyzing}
            />
            <div className="flex-1">
              <Label htmlFor="binaryMode" className="text-sm font-medium">
                Binary Mode (Honest Engine Compatible)
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                {binaryMode ? 
                  "Patterns use binary 1/0 format, matching honest engine logic and confidence formula" :
                  "Patterns use side-aware 1L/0L/1S/0S format with directional analysis"
                }
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {!file.raw_data && (
                <span className="text-red-500">No raw data available - please re-upload the file</span>
              )}
            </div>
            
            <Button 
              onClick={runAnalysis}
              disabled={isAnalyzing || !file.raw_data || file.analysis_status === 'processing'}
              className="flex items-center gap-2"
            >
              {isAnalyzing || file.analysis_status === 'processing' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {isAnalyzing || file.analysis_status === 'processing' 
                ? 'Analyzing...' 
                : 'Run Analysis'
              }
            </Button>
          </div>

          {(isAnalyzing || file.analysis_status === 'processing') && (
            <div className="text-sm text-muted-foreground">
              Analysis is running... This may take a few seconds depending on the complexity.
            </div>
          )}

          {complexityWarning && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800">
                <Info className="h-4 w-4" />
                <span className="font-medium">Complexity Notice</span>
              </div>
              <p className="text-sm text-blue-600 mt-1">{complexityWarning}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};